<template>
  <div class="terms-container">
    <!-- Label -->
    <label v-if="label" class="terms-label">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <!-- Scroll wrapper with gradient fade -->
    <div
      class="terms-scroll-wrapper"
      :class="{ 'scrolled-bottom': isAtBottom }"
    >
      <div
        ref="scrollBox"
        class="terms-text"
        :style="{ height: (config.scroll_height || 280) + 'px' }"
        v-html="config.terms_content || ''"
        @scroll="onScroll"
      ></div>
    </div>

    <!-- Progress bar -->
    <div class="scroll-progress">
      <div class="scroll-progress-bar" :style="{ width: scrollProgress + '%' }"></div>
    </div>

    <!-- Footer -->
    <div class="terms-footer">
      <!-- Scroll hint -->
      <div
        v-if="requireScroll"
        class="scroll-hint"
        :class="{ hidden: hasScrolledToBottom }"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 3v10M4 9l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Role até o final para habilitar o aceite
      </div>

      <!-- Accept row.
           Estrutura: label envolve input + span do texto. Toggling é controlado
           SÓ pelo @change do <input>, lendo event.target.checked (estado real
           do DOM). Não usar @click.prevent no label nem toggle baseado em
           !modelValue: combinação dispara double-fire/race em Safari, fazendo
           a validação rodar com valor stale e exibir erro mesmo com checkbox
           marcando. Click no texto/área-livre cai no behavior HTML nativo do
           label (dispara click sintético no input filho → @change). -->
      <label
        class="accept-row"
        :class="{
          checked: modelValue === true,
          disabled: requireScroll && !hasScrolledToBottom
        }"
      >
        <div class="custom-checkbox">
          <input
            type="checkbox"
            :checked="modelValue === true"
            :disabled="(requireScroll && !hasScrolledToBottom) || disabled"
            @change="onCheckboxChange"
          />
          <span class="checkmark"></span>
        </div>
        <span class="accept-label">
          {{ config.checkbox_label || 'Declaro que li e concordo integralmente com os termos descritos acima' }}
        </span>
      </label>
    </div>

    <!-- Error message -->
    <p v-if="hasError" class="terms-error">
      {{ errorMessage || 'Você deve ler e aceitar os termos para prosseguir.' }}
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';

export default {
  name: 'ITermsAcceptance',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    config: {
      type: Object,
      default: () => ({})
    },
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const scrollBox = ref(null);
    const scrollProgress = ref(0);
    const hasScrolledToBottom = ref(false);
    const isAtBottom = ref(false);

    const requireScroll = computed(() => {
      return props.config.require_scroll !== false;
    });

    const onScroll = () => {
      if (!scrollBox.value) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollBox.value;
      const maxScroll = scrollHeight - clientHeight;

      if (maxScroll <= 0) {
        // Content fits without scroll
        scrollProgress.value = 100;
        hasScrolledToBottom.value = true;
        isAtBottom.value = true;
        return;
      }

      const progress = (scrollTop / maxScroll) * 100;
      scrollProgress.value = Math.min(progress, 100);

      if (scrollTop + clientHeight >= scrollHeight - 10) {
        isAtBottom.value = true;
        if (!hasScrolledToBottom.value) {
          hasScrolledToBottom.value = true;
        }
      } else {
        isAtBottom.value = false;
      }
    };

    const onCheckboxChange = (event) => {
      // Se disabled ou ainda sem scroll completo, reverte o estado do DOM
      // (defensivo — :disabled já bloqueia click, mas garante consistência
      // se algum browser tunelar o evento).
      if (props.disabled || (requireScroll.value && !hasScrolledToBottom.value)) {
        event.target.checked = props.modelValue === true;
        return;
      }
      emit('update:modelValue', event.target.checked);
    };

    onMounted(() => {
      nextTick(() => {
        if (scrollBox.value) {
          const { scrollHeight, clientHeight } = scrollBox.value;
          if (scrollHeight <= clientHeight) {
            hasScrolledToBottom.value = true;
            isAtBottom.value = true;
            scrollProgress.value = 100;
          }
        }
      });
    });

    return {
      scrollBox,
      scrollProgress,
      hasScrolledToBottom,
      isAtBottom,
      requireScroll,
      onScroll,
      onCheckboxChange
    };
  }
};
</script>

<style scoped>
.terms-container {
  width: 100%;
}

.terms-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.terms-scroll-wrapper {
  position: relative;
}

.terms-scroll-wrapper::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 48px;
  background: linear-gradient(to top, #ffffff, transparent);
  pointer-events: none;
  z-index: 1;
  transition: opacity 0.3s ease;
}

.terms-scroll-wrapper.scrolled-bottom::after {
  opacity: 0;
}

.terms-text {
  overflow-y: auto;
  padding: 20px 24px;
  padding-bottom: 32px;
  background: #ffffff;
  border: 1px solid #e8e4de;
  border-radius: 12px;
  font-size: 0.8125rem;
  line-height: 1.75;
  color: #4a453f;
  scroll-behavior: smooth;
}

.terms-text :deep(h3) {
  font-size: 0.875rem;
  font-weight: 700;
  color: #2c2926;
  margin: 20px 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.terms-text :deep(h3:first-child) {
  margin-top: 0;
}

.terms-text :deep(p) {
  margin-bottom: 12px;
}

.scroll-progress {
  height: 3px;
  background: #e8e4de;
  border-radius: 2px;
  overflow: hidden;
}

.scroll-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #1a7a5c, #24a87a);
  border-radius: 2px;
  transition: width 0.1s ease;
}

.terms-footer {
  padding: 16px 0 0;
}

.scroll-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fef8ec;
  border: 1px solid #f0ddb0;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.8125rem;
  color: #8a6d2b;
  transition: all 0.4s ease;
}

.scroll-hint.hidden {
  opacity: 0;
  height: 0;
  padding: 0 16px;
  margin-bottom: 0;
  border-color: transparent;
  overflow: hidden;
}

.scroll-hint svg {
  flex-shrink: 0;
  animation: bounce 1.5s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}

.accept-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 14px 16px;
  border-radius: 10px;
  border: 2px solid #e8e4de;
  transition: all 0.25s ease;
  user-select: none;
  margin-bottom: 0;
}

.accept-row:hover {
  border-color: #c4bfb7;
  background: #fdfcfb;
}

.accept-row.checked {
  border-color: #1a7a5c;
  background: #f0faf5;
}

.accept-row.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.accept-row.disabled:hover {
  border-color: #e8e4de;
  background: transparent;
}

.custom-checkbox {
  position: relative;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-top: 1px;
}

.custom-checkbox input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  z-index: 1;
}

.custom-checkbox input:disabled {
  cursor: not-allowed;
}

.checkmark {
  position: absolute;
  inset: 0;
  background: #ffffff;
  border: 2px solid #ccc8c1;
  border-radius: 6px;
  transition: all 0.25s ease;
}

.custom-checkbox input:checked ~ .checkmark {
  background: #1a7a5c;
  border-color: #1a7a5c;
}

.checkmark::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 11px;
  border: solid #fff;
  border-width: 0 2.5px 2.5px 0;
  transform: rotate(45deg) scale(0);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.custom-checkbox input:checked ~ .checkmark::after {
  transform: rotate(45deg) scale(1);
}

.accept-label {
  font-size: 0.875rem;
  line-height: 1.55;
  color: #3d3833;
}

.terms-error {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 8px;
}

@media (max-width: 480px) {
  .terms-text {
    padding: 16px;
    font-size: 0.8rem;
  }
}
</style>
